import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import client from "../../api/client";

// Simple real-time via polling — fetches every 5 s while the tab is open
function usePolling(fn, delay = 5000) {
  const saved = useRef(fn);
  useEffect(() => { saved.current = fn; }, [fn]);
  useEffect(() => {
    const id = setInterval(() => saved.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
}

function Avatar({ name, img }) {
  const initials = (name || "?").split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase();
  if (img) return <img src={img} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />;
  return <div className="w-8 h-8 rounded-full bg-[#294a93] text-white text-xs flex items-center justify-center font-bold flex-shrink-0">{initials}</div>;
}

function CommentRow({ comment, onReply, onDelete }) {
  const name = `${comment.firstname || ""} ${comment.lastname || ""}`.trim() || comment.email;
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [posting, setPosting] = useState(false);

  const postReply = async e => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setPosting(true);
    try {
      await client.post(`/schedules/${comment.schedule_id}/comments`, { comment: replyText, parent_id: comment.id });
      toast.success("Reply posted.");
      setReplyText(""); setReplyOpen(false);
      onReply();
    } catch { toast.error("Failed to post reply."); }
    finally { setPosting(false); }
  };

  return (
    <div className={`bg-white rounded-xl border border-gray-100 p-4 ${comment.parent_id ? "ml-8 border-l-4 border-l-[#294a93]/20" : ""}`}>
      <div className="flex items-start gap-3">
        <Avatar name={name} img={comment.profile_img} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-800">{name}</span>
            <span className="text-xs text-gray-400">{comment.email}</span>
            {comment.course_title && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{comment.course_title}</span>}
            <span className="text-xs text-gray-300 ml-auto">{new Date(comment.created_at).toLocaleString()}</span>
          </div>
          <p className="text-sm text-gray-700 mt-1 leading-relaxed">{comment.comment}</p>
          <div className="flex gap-3 mt-2">
            {!comment.parent_id && (
              <button onClick={() => setReplyOpen(r => !r)} className="text-xs text-[#294a93] font-semibold hover:underline">Reply</button>
            )}
            <button onClick={() => onDelete(comment.id)} className="text-xs text-red-500 font-semibold hover:underline">Delete</button>
          </div>
          {replyOpen && (
            <form onSubmit={postReply} className="mt-3 flex gap-2">
              <input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Write a reply…"
                className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
              <button type="submit" disabled={posting || !replyText.trim()} className="bg-[#294a93] text-white text-xs font-bold px-4 py-2 rounded-xl disabled:opacity-50 transition-colors">
                {posting ? "…" : "Send"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminCommentsPage() {
  const [comments, setComments] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [newCount, setNewCount] = useState(0);
  const lastFetchRef = useRef(null);

  const load = useCallback((p = 1, silent = false) => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    if (!silent) setLoading(true);
    client.get("/admin/comments", { params: { search, page: p } })
      .then(res => {
        const incoming = res.data.data || [];
        if (silent && lastFetchRef.current) {
          const fresh = incoming.filter(c => new Date(c.created_at) > lastFetchRef.current);
          if (fresh.length > 0) setNewCount(n => n + fresh.length);
        }
        lastFetchRef.current = new Date();
        setComments(incoming);
        setMeta(res.data);
        setPage(p);
      })
      .catch(() => { if (!silent) toast.error("Failed to load comments."); })
      .finally(() => { if (!silent) setLoading(false); });
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setNewCount(0); load(1); }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll every 5 seconds for new comments
  usePolling(() => load(page, true), 5000);

  const handleDelete = async id => {
    if (!window.confirm("Delete this comment?")) return;
    try { await client.delete(`/comments/${id}`); toast.success("Deleted."); load(page); } catch { toast.error("Failed."); }
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Comments</h1>
          <p className="text-sm text-gray-500 mt-0.5">All comments across every session — updates every 5 seconds</p>
        </div>
        <div className="flex items-center gap-3">
          {newCount > 0 && (
            <button onClick={() => { setNewCount(0); load(1); }} className="text-xs bg-[#294a93] text-white font-bold px-3 py-2 rounded-xl animate-pulse">
              {newCount} new — refresh
            </button>
          )}
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search comments…"
            className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 w-56" />
        </div>
      </div>

      <div className="space-y-3">
        {loading ? (
          [1,2,3,4].map(i => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)
        ) : comments.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
            <p className="text-4xl mb-3">💬</p>
            <p className="text-sm font-medium text-gray-600">No comments yet</p>
          </div>
        ) : (
          comments.map(c => (
            <CommentRow key={c.id} comment={c} onReply={() => load(page)} onDelete={handleDelete} />
          ))
        )}
      </div>

      {meta && meta.last_page > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button disabled={page === 1} onClick={() => load(page - 1)} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors">← Prev</button>
          <span className="px-4 py-2 text-sm text-gray-500">Page {page} of {meta.last_page}</span>
          <button disabled={page === meta.last_page} onClick={() => load(page + 1)} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors">Next →</button>
        </div>
      )}
    </div>
  );
}
